/**
 * Phase 2: the full, resumable vendor onboarding wizard.
 *
 * Route: /cab-driver-registration/onboarding
 * Arrives from Phase 1 with ?m=<mobile> (and optional prefill). Every step is
 * autosaved to localStorage keyed by mobile, so a vendor can leave and return.
 * Uploads and the final submit call the contract in store/Partner/partnerApi.js
 * and degrade gracefully while that backend is still being built.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useSearchParams, Link } from "react-router-dom"
import { Helmet } from "react-helmet"
import { toast } from "react-toastify"

import Navbar from "../../layout/Navbar"
import Footer from "../../layout/Footer"
import { isValidMobileNumber } from "../../utils/mobileNumberUtils"
import { WIZARD_STEPS, businessType, documentsForBusiness } from "../../utils/partnerOnboardingConfig"
import { loadDraft, saveDraft, clearDraft } from "../../utils/partnerDraft"
import { uploadPartnerDocument, submitPartnerOnboarding, sendPartnerOtp, verifyPartnerOtp } from "../../store/Partner/partnerApi"
import {
  BusinessTypeStep, BusinessTaxStep, FleetStep, RateCardStep, DocumentsStep, BankStep, ReviewStep,
} from "./Steps"
// Marketing sections rendered BELOW the wizard so the page still carries its
// benefits / how-it-works / FAQ content for SEO and context.
import BenefitsPartner from "../PartnerNew/BenefitsPartner"
import HowItWorks from "../PartnerNew/HowItWorks"
import EarningsPartner from "../PartnerNew/EarningsPartner"
import DocumentsPartner from "../PartnerNew/DocumentsPartner"
import TestimonialsPartner from "../PartnerNew/TestimonialsPartner"
import FaqPartner from "../PartnerNew/FaqPartner"
import DownloadPartner from "../PartnerNew/DownloadPartner"
import "./partnerOnboarding.scss"

const emptyData = {
  businessType: "",
  legalName: "", contactName: "", email: "", mobile: "",
  pan: "", gstin: "", cin: "", incorporationDate: "",
  addressLine: "", city: "", state: "", pincode: "",
  cabTypesOffered: [], numberOfCabs: "", vehicles: [],
  rates: {}, topRoutes: [],
  documents: {}, bank: {},
}

const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || "")

const PartnerOnboarding = () => {
  const [params] = useSearchParams()
  const queryMobile = (params.get("m") || "").replace(/\D/g, "")
  const [pid, setPid] = useState(params.get("pid") || "")

  const [mobile] = useState(queryMobile)
  const [data, setData] = useState(emptyData)
  const [stepIndex, setStepIndex] = useState(0)
  const [errors, setErrors] = useState({})
  const [docStatus, setDocStatus] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(null) // { serverAccepted: bool }
  // Verify-at-submit: the vendor fills all 7 steps freely, then a single OTP on
  // their mobile confirms the number just before the application is sent.
  const [verified, setVerified] = useState(false)
  const [otpStage, setOtpStage] = useState("idle") // idle | otp
  const [otp, setOtp] = useState("")
  const [otpRequestId, setOtpRequestId] = useState("")

  // Hydrate from a saved draft (+ any Phase-1 prefill from the URL). No mobile
  // gate anymore: a fresh vendor lands straight on step 1; a returning one is
  // resumed from ?m= or the most recent local ("anon") draft.
  useEffect(() => {
    const src = mobile || "anon"
    const draft = loadDraft(src)
    const prefill = {
      ...(mobile ? { mobile, _mobileLocked: true } : {}),
      legalName: params.get("name") || "",
      email: params.get("email") || "",
      city: params.get("city") || "",
      state: params.get("state") || "",
      businessType: params.get("type") === "fleet" ? "proprietor" : "",
    }
    setData({ ...emptyData, ...prefill, ...(draft || {}) })
    // Rebuild doc status from any previously uploaded docs.
    if (draft?.documents) {
      const st = {}
      Object.keys(draft.documents).forEach((k) => { st[k] = "done" })
      setDocStatus(st)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile])

  const update = useCallback((patch) => {
    setData((prev) => {
      const next = { ...prev, ...patch }
      const key = next.mobile && isValidMobileNumber(next.mobile) ? next.mobile : (mobile || "anon")
      saveDraft(key, next)
      return next
    })
  }, [mobile])

  /* ---- validation -------------------------------------------------- */

  const validate = useCallback((index) => {
    const e = {}
    const bt = businessType(data.businessType)
    if (index === 0) {
      if (!data.businessType) e.businessType = "Pick one to continue"
    }
    if (index === 1) {
      if (!data.legalName?.trim()) e.legalName = "Required"
      if (!emailOk(data.email)) e.email = "Enter a valid email"
      if (!isValidMobileNumber(data.mobile)) e.mobile = "Enter a valid 10-digit mobile"
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(data.pan || "")) e.pan = "Enter a valid PAN"
      if (bt.gstRequired && !/^[0-9A-Z]{15}$/.test(data.gstin || "")) e.gstin = "15-character GSTIN required"
      if (bt.incorporationRequired && !data.cin?.trim()) e.cin = "Required for a company"
      if (!data.addressLine?.trim()) e.addressLine = "Required"
      if (!data.city) e.city = "Required"
      if (!data.state) e.state = "Required"
      if (!/^[0-9]{6}$/.test(data.pincode || "")) e.pincode = "6-digit PIN"
    }
    if (index === 2) {
      if (!(data.cabTypesOffered || []).length) e.cabTypesOffered = "Choose at least one"
      if (!data.numberOfCabs || Number(data.numberOfCabs) < 1) e.numberOfCabs = "At least 1"
    }
    if (index === 3) {
      const missing = (data.cabTypesOffered || []).some((c) => !(Number(data.rates?.[c]?.perKmOneWay) > 0))
      if (missing) e.rates = "Set a one-way per-km rate for every cab type you offer"
    }
    if (index === 4) {
      const groups = documentsForBusiness(data.businessType || "individual")
      const required = Object.values(groups).flat().filter((d) => d.required)
      const missing = required.filter((d) => !data.documents?.[d.key])
      if (missing.length) e._docs = `Upload the required documents: ${missing.map((d) => d.label.split(" (")[0]).join(", ")}`
    }
    if (index === 5) {
      if (!data.bank?.accountName?.trim()) e.accountName = "Required"
      if (!/^[0-9]{6,18}$/.test(data.bank?.accountNumber || "")) e.accountNumber = "Enter a valid account number"
      if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(data.bank?.ifsc || "")) e.ifsc = "Enter a valid IFSC"
    }
    return e
  }, [data])

  const next = () => {
    const e = validate(stepIndex)
    setErrors(e)
    if (Object.keys(e).length) {
      toast.error(e._docs || "Please fix the highlighted fields")
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }
    setStepIndex((i) => Math.min(i + 1, WIZARD_STEPS.length - 1))
    window.scrollTo({ top: 0, behavior: "smooth" })
  }
  const back = () => { setErrors({}); setStepIndex((i) => Math.max(i - 1, 0)) }
  const goTo = (i) => { setErrors({}); setStepIndex(i); window.scrollTo({ top: 0, behavior: "smooth" }) }

  /* ---- document uploads ------------------------------------------- */

  const onDocSelect = async (docKey, file) => {
    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10 MB)")
    setDocStatus((s) => ({ ...s, [docKey]: "uploading" }))
    const res = await uploadPartnerDocument({ partner_id: pid, doc_key: docKey, file })
    if (res.ok) {
      update({ documents: { ...data.documents, [docKey]: res.data } })
      setDocStatus((s) => ({ ...s, [docKey]: "done" }))
    } else if (res.notImplemented) {
      // Backend upload endpoint not live yet: keep the selection locally so the
      // vendor can finish, and mark it pending for the eventual server sync.
      update({ documents: { ...data.documents, [docKey]: { filename: file.name, size: file.size, pending: true } } })
      setDocStatus((s) => ({ ...s, [docKey]: "done" }))
    } else {
      setDocStatus((s) => ({ ...s, [docKey]: "error" }))
      toast.error(res.message || "Upload failed")
    }
  }
  const onDocRemove = (docKey) => {
    const docs = { ...data.documents }
    delete docs[docKey]
    update({ documents: docs })
    setDocStatus((s) => ({ ...s, [docKey]: "idle" }))
  }

  /* ---- submit ------------------------------------------------------ */

  // Actually send the application — only reached once the mobile is verified.
  const doSubmit = async () => {
    setSubmitting(true)
    const payload = { ...data, partner_id: pid || undefined, source: "website", submittedAt: new Date().toISOString() }
    const res = await submitPartnerOnboarding({ partner_id: pid, payload })
    setSubmitting(false)
    if (res.ok) {
      clearDraft(data.mobile); clearDraft("anon")
      setOtpStage("idle")
      setSubmitted({ serverAccepted: true })
    } else if (res.notImplemented) {
      // Keep the draft so nothing is lost until the endpoint is live.
      setOtpStage("idle")
      setSubmitted({ serverAccepted: false })
    } else {
      toast.error(res.message || "Could not submit, please try again")
    }
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const submit = async () => {
    // Re-validate every gated step before the final send.
    for (let i = 1; i <= 5; i++) {
      const e = validate(i)
      if (Object.keys(e).length) {
        setErrors(e); goTo(i); toast.error("Some details are missing, please complete this step")
        return
      }
    }
    if (verified) return doSubmit()
    // Confirm the mobile with a one-time OTP before the application is sent.
    setSubmitting(true)
    const sent = await sendPartnerOtp(data.mobile)
    setSubmitting(false)
    if (sent.ok) {
      setOtpRequestId(sent.data?.request_id || "")
      setOtp("")
      setOtpStage("otp")
      toast.success(`OTP sent to ${data.mobile}`)
    } else if (sent.notImplemented) {
      // OTP service not live yet — never block the vendor; submit gracefully.
      setVerified(true)
      doSubmit()
    } else {
      toast.error(sent.message || "Could not send OTP, please try again")
    }
  }

  const confirmOtp = async () => {
    if (!/^\d{4,6}$/.test(otp)) return toast.error("Enter the OTP you received")
    setSubmitting(true)
    const res = await verifyPartnerOtp({ mobile_number: data.mobile, otp, request_id: otpRequestId })
    setSubmitting(false)
    if (res.ok || res.notImplemented) {
      if (res.data?.partner_id) setPid(res.data.partner_id)
      setVerified(true)
      doSubmit()
    } else {
      toast.error(res.message || "Incorrect OTP, please try again")
    }
  }

  const resendOtp = async () => {
    const sent = await sendPartnerOtp(data.mobile)
    if (sent.ok) { setOtpRequestId(sent.data?.request_id || ""); toast.success("OTP resent") }
    else if (sent.notImplemented) { setVerified(true); doSubmit() }
    else toast.error(sent.message || "Could not resend OTP")
  }

  const stepProps = { data, update, errors, docStatus, onDocSelect, onDocRemove, goTo }
  const CurrentStep = useMemo(() => ([
    <BusinessTypeStep {...stepProps} />,
    <BusinessTaxStep {...stepProps} />,
    <FleetStep {...stepProps} />,
    <RateCardStep {...stepProps} />,
    <DocumentsStep {...stepProps} />,
    <BankStep {...stepProps} />,
    <ReviewStep {...stepProps} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ])[stepIndex], [stepIndex, data, errors, docStatus])

  /* ---- success ----------------------------------------------------- */

  if (submitted) {
    return (
      <div className="partner-ob">
        <Navbar page="partner" />
        <div className="partner-ob__done">
          <div className="partner-ob__done-icon" aria-hidden>&#10003;</div>
          <h1>Application submitted</h1>
          <p>
            Thanks{data.legalName ? `, ${data.legalName.split(" ")[0]}` : ""}. Your vendor application is in.
            Our onboarding team verifies documents and activates approved partners within 24 to 48 hours.
          </p>
          {!submitted.serverAccepted && (
            <p className="partner-ob__done-note">
              Saved on this device. It will sync to Pulpit for review automatically once you are online with our latest app update.
            </p>
          )}
          <Link to="/" className="partner-ob__done-btn">Back to home</Link>
        </div>
        <Footer />
      </div>
    )
  }

  /* ---- wizard ------------------------------------------------------ */

  const pct = Math.round(((stepIndex + 1) / WIZARD_STEPS.length) * 100)
  const isLast = stepIndex === WIZARD_STEPS.length - 1

  return (
    <div className="partner-ob">
      <Helmet><title>Register as a Cab Driver or Fleet Partner | Pulpit</title><meta name="description" content="Attach your car or fleet to Pulpit. Register in 7 simple steps, get verified, and start earning on outstation and local cab bookings across India." /></Helmet>
      <Navbar page="partner" />

      <div className="partner-ob__wrap">
        <div className="partner-ob__head">
          <h1 className="partner-ob__title">Vendor registration</h1>
          <span className="partner-ob__save">Progress saved automatically</span>
        </div>

        {/* Stepper */}
        <div className="partner-ob__stepper">
          <div className="partner-ob__bar"><span style={{ width: `${pct}%` }} /></div>
          <ol className="partner-ob__steps">
            {WIZARD_STEPS.map((s, i) => (
              <li
                key={s.id}
                className={`partner-ob__stepitem${i === stepIndex ? " is-current" : ""}${i < stepIndex ? " is-done" : ""}`}
                onClick={() => i < stepIndex && goTo(i)}
              >
                <span className="partner-ob__stepnum">{i < stepIndex ? "✓" : i + 1}</span>
                <span className="partner-ob__steplabel">{s.short}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Current step */}
        <div className="partner-ob__panel">
          {errors._docs && <div className="pw-note pw-note--err">{errors._docs}</div>}
          {CurrentStep}
        </div>

        {/* Nav */}
        <div className="partner-ob__nav">
          <button className="partner-ob__btn partner-ob__btn--ghost" onClick={back} disabled={stepIndex === 0}>
            Back
          </button>
          {isLast ? (
            <button className="partner-ob__btn partner-ob__btn--primary" onClick={submit} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit application"}
            </button>
          ) : (
            <button className="partner-ob__btn partner-ob__btn--primary" onClick={next}>
              Continue
            </button>
          )}
        </div>
      </div>

      {/* Marketing / SEO content, kept below the wizard */}
      <BenefitsPartner />
      <HowItWorks />
      <EarningsPartner />
      <DocumentsPartner />
      <TestimonialsPartner />
      <FaqPartner />
      <DownloadPartner />

      {/* Verify-at-submit: one OTP confirms the mobile just before sending */}
      {otpStage === "otp" && (
        <div
          role="dialog"
          aria-modal="true"
          style={{ position: "fixed", inset: 0, background: "rgba(8,32,29,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
        >
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 26px", maxWidth: 380, width: "100%", boxShadow: "0 24px 60px -20px rgba(0,0,0,.4)", textAlign: "center" }}>
            <h2 style={{ margin: "0 0 8px", fontSize: 22 }}>Verify your mobile</h2>
            <p style={{ margin: "0 0 16px", color: "#5b6b68", fontSize: 14 }}>
              Enter the OTP sent to <b>{data.mobile}</b> to submit your application.
            </p>
            <input
              className="pw-input"
              inputMode="numeric"
              placeholder="Enter OTP"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              style={{ textAlign: "center", letterSpacing: "4px", fontSize: 18, marginBottom: 14 }}
            />
            <button className="partner-ob__btn partner-ob__btn--primary" style={{ width: "100%" }} onClick={confirmOtp} disabled={submitting}>
              {submitting ? "Verifying..." : "Verify & submit"}
            </button>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, fontSize: 13 }}>
              <button type="button" onClick={resendOtp} style={{ background: "none", border: "none", color: "#21a5a0", cursor: "pointer", fontWeight: 600 }}>Resend OTP</button>
              <button type="button" onClick={() => { setOtpStage("idle"); setSubmitting(false) }} style={{ background: "none", border: "none", color: "#5b6b68", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}

export default PartnerOnboarding
